"use client";

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";

import {
    updateDictationToolVisibility,
    type DictationVisibilityResult,
} from "@/lib/actions/dictation-preference-actions";

interface DictationVisibilityContextValue {
    isVisible: boolean;
    hide: () => Promise<DictationVisibilityResult>;
}

const DictationVisibilityContext =
    createContext<DictationVisibilityContextValue | null>(null);

export function DictationVisibilityProvider({
    initialVisible,
    children,
}: {
    initialVisible: boolean;
    children: ReactNode;
}) {
    const [isVisible, setIsVisible] = useState(initialVisible);
    const hide = useCallback(async () => {
        const result = await updateDictationToolVisibility(false);
        if ("success" in result) setIsVisible(result.isVisible);
        return result;
    }, []);
    const value = useMemo(
        () => ({ isVisible, hide }),
        [hide, isVisible],
    );

    return (
        <DictationVisibilityContext.Provider value={value}>
            {children}
        </DictationVisibilityContext.Provider>
    );
}

export function useDictationVisibility(): DictationVisibilityContextValue {
    const value = useContext(DictationVisibilityContext);
    if (!value) {
        throw new Error(
            "useDictationVisibility must be used within DictationVisibilityProvider",
        );
    }
    return value;
}
